import { create } from "zustand"
import { devtools } from "zustand/middleware"
import { IGetEmployeeByIdParams, IPaginationMeta, ISapProduct, ISapProductDto, ISapProductPreview, ISapProductUpdateDto } from "../../types"
import { sapProductsService } from "../../apis/services"

interface IProductState {
  data: ISapProduct[]
  meta?: IPaginationMeta
  isLoading: boolean
  previewData?: ISapProductPreview
  error: string | null
  productDetail?: ISapProduct | null
  fetchProducts: (params: IGetEmployeeByIdParams) => Promise<void>
  fetchProductByRecordId: (recordId: string) => Promise<void>
  createProduct: (dto: ISapProductDto) => Promise<void>
  updateProduct: (recordId: string, dto: ISapProductUpdateDto) => Promise<void>
  previewProduct: (file: File) => Promise<void>
  clearError: () => void
  clearPreview: () => void
  reset: () => void
}

const initialState = {
  data: [] as ISapProduct[],
  meta: undefined,
  isLoading: false,
  previewData: undefined,
  error: null,
  productDetail: null,
}

export const useSapProductStore = create<IProductState>()(
  devtools((set) => ({
    ...initialState,

    fetchProducts: async (params: IGetEmployeeByIdParams) => {
      set({ isLoading: true, error: null })

      try {
        const response = await sapProductsService.getMasterProducts(params)
        set({ data: response.data.items, meta: response.data.meta })
      } catch (error) {
        const errorMessage: string = error instanceof Error ? error.message : "An unknown error occurred"
        set({ error: errorMessage })
        throw error
      } finally {
        set({ isLoading: false })
      }
    },

    fetchProductByRecordId: async (recordId: string) => {
      set({ isLoading: true, error: null })

      try {
        const product = await sapProductsService.getMasterProductByRecordId(recordId)
        set({ productDetail: product.data })
      } catch (error) {
        const errorMessage: string = error instanceof Error ? error.message : "An unknown error occurred"
        set({ error: errorMessage })
        throw error
      } finally {
        set({ isLoading: false })
      } },

    createProduct: async (dto: ISapProductDto) => {
      set({ isLoading: true, error: null })
      try {
        await sapProductsService.createMasterProduct(dto)
      } catch (error) {
        const errorMessage: string = error instanceof Error ? error.message : "An unknown error occurred"
        set({ error: errorMessage })
        throw error
      } finally {
        set({ isLoading: false })
      } },

    updateProduct: async (recordId: string, dto: ISapProductUpdateDto) => {
      set({ isLoading: true, error: null })
      try {
        const response = await sapProductsService.updateMasterProduct(recordId, dto)
        set({ productDetail: response.data })
      } catch (error) {
        const errorMessage: string = error instanceof Error ? error.message : "An unknown error occurred"
        set({ error: errorMessage })
        throw error
      } finally {
        set({ isLoading: false })
      } },

    previewProduct: async (file: File) => {
      set({ isLoading: true, error: null })
      try {
        const previewData = await sapProductsService.previewProduct(file)
        set({ previewData })
      } catch (error) {
        const errorMessage: string = error instanceof Error ? error.message : "An unknown error occurred"
        set({ error: errorMessage })
        throw error
      } finally {
        set({ isLoading: false })
      } },

    clearError: () => {
      set({ error: null })
    },

    clearPreview: () => {
      set({ previewData: undefined })
    },

    reset: () => {
      set(initialState)
    },
  }))
)
